// src/pages/SignupScreen.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useApp } from '../lib/AppContext';
import { t } from '../lib/i18n';

export default function SignupScreen() {
  const { lang, showToast } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm: '' });
  const [gdpr, setGdpr] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) { showToast('Please fill in all fields', 'error'); return; }
    if (form.password !== form.confirm) { showToast('Passwords do not match', 'error'); return; }
    if (form.password.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
    if (!gdpr) { showToast('Please accept the Terms & Conditions to continue', 'error'); return; }
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: { full_name: form.full_name, marketing_consent: marketing },
        emailRedirectTo: `${window.location.origin}/account`,
      },
    });
    if (error) { showToast(error.message, 'error'); setLoading(false); return; }

    const now = new Date().toISOString();

    // Always upsert subscriber row so the email is captured
    await supabase.from('newsletter_subscribers').upsert({
      email:           form.email.trim().toLowerCase(),
      name:            form.name || form.full_name || null,
      active:          true,
      language:        'en',
      country:         'DE',
      source:          'signup',
      gdpr_consent:    true,          // signup = GDPR acceptance
      gdpr_consent_at: now,
      subscribed_at:   now,
    }, { onConflict: 'email', ignoreDuplicates: false });

    // Grant +1 spin for newsletter signup (one-time, duplicate-guarded)
    setTimeout(async () => {
      try {
        const { data: prof } = await supabase
          .from('profiles')
          .select('id, spin_balance, newsletter_spin_claimed')
          .eq('email', form.email)
          .maybeSingle();
        if (prof && !prof.newsletter_spin_claimed) {
          await supabase.from('profiles').update({
            spin_balance:            (prof.spin_balance || 0) + 1,
            newsletter_spin_claimed: true,
          }).eq('id', prof.id);
        }
      } catch (_) { /* profile may not exist yet — trigger handles it */ }
    }, 2000);

    showToast('Account created! Check your email to verify. 🎰 +1 spin added!', 'success');
    navigate('/login');
  };

  const handleOAuth = async (provider) => {
    if (!gdpr) { showToast('Please accept the Terms & Conditions first', 'error'); return; }
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/account` },
    });
    if (error) showToast(error.message, 'error');
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.6rem', letterSpacing: '.08em', color: '#4cca74', marginBottom: 6 }}>CosmiqHerbz</div>
          <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '2.2rem', letterSpacing: '.05em', marginBottom: 8 }}>{t('create_account', lang)}</h1>
          <p style={{ color: '#666', fontSize: '.88rem', fontWeight: 300 }}>
            {t('already_account', lang)}{' '}
            <Link to="/login" style={{ color: '#4cca74', fontWeight: 600, textDecoration: 'none' }}>{t('sign_in', lang)}</Link>
          </p>
        </div>

        <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div className="input-label">{t('full_name', lang)}</div><input className="input-field" value={form.full_name} onChange={e => update('full_name', e.target.value)} autoComplete="name" /></div>
          <div><div className="input-label">{t('email', lang)}</div><input className="input-field" type="email" value={form.email} onChange={e => update('email', e.target.value)} autoComplete="email" /></div>
          <div><div className="input-label">{t('password', lang)}</div><input className="input-field" type="password" value={form.password} onChange={e => update('password', e.target.value)} autoComplete="new-password" /></div>
          <div><div className="input-label">{t('confirm_password', lang)}</div><input className="input-field" type="password" value={form.confirm} onChange={e => update('confirm', e.target.value)} autoComplete="new-password" /></div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginTop: 4 }}>
            <input type="checkbox" checked={gdpr} onChange={e => setGdpr(e.target.checked)} style={{ marginTop: 3, accentColor: '#4cca74', width: 15, height: 15, flexShrink: 0 }} />
            <span style={{ fontSize: '.8rem', color: '#777', lineHeight: 1.7, fontWeight: 300 }}>
              {t('gdpr_consent', lang)}{' '}
              <Link to="/legal/terms" style={{ color: '#4cca74' }}>Terms</Link> &{' '}
              <Link to="/legal/privacy" style={{ color: '#4cca74' }}>Privacy Policy</Link>.
            </span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={marketing} onChange={e => setMarketing(e.target.checked)} style={{ marginTop: 3, accentColor: '#4cca74', width: 15, height: 15, flexShrink: 0 }} />
            <span style={{ fontSize: '.8rem', color: '#555', lineHeight: 1.7, fontWeight: 300 }}>
              {t('marketing_consent', lang)}{' '}
              <span style={{ color: '#4cca74', fontSize: '.72rem' }}>🎰 +1 free spin</span>
            </span>
          </label>

          <button className="btn-primary" type="submit" disabled={loading} style={{ width: '100%', marginTop: 4 }}>
            {loading ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2, margin: '0 auto' }} /> : t('create_account', lang)}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#222' }} />
          <span style={{ color: '#444', fontSize: '.75rem', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.1em', textTransform: 'uppercase' }}>{t('or_continue', lang)}</span>
          <div style={{ flex: 1, height: 1, background: '#222' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => handleOAuth('google')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#fff', color: '#111', border: 'none', borderRadius: 25, padding: '12px 24px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', width: '100%' }}>
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            {t('google', lang)}
          </button>
          <button onClick={() => handleOAuth('apple')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: '#111', color: '#fff', border: '1px solid #333', borderRadius: 25, padding: '12px 24px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', width: '100%' }}>
            <svg width="18" height="18" viewBox="0 0 256 256" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M213.1 78.9c-1.5 1.1-27.5 15.8-27.5 48.4 0 37.7 33.1 51.1 34 51.4-.1.8-5.3 18.3-17.5 36.1-10.8 15.7-22.2 31.3-39.5 31.3s-21.7-10.1-41.7-10.1c-19.5 0-26.4 10.4-42.2 10.4S55.4 235 43 217.5C28.8 197.9 17 166.9 17 137.5c0-49.3 32.2-75.4 63.8-75.4 16.8 0 30.8 11.1 41.4 11.1 10.1 0 25.9-11.8 44.9-11.8 7.2 0 33.2.7 50.2 25.2zm-58.7-47.6C162.6 22.3 169 11 169 0c0-.8-.1-1.7-.2-2.5-9.1.3-19.9 6.1-26.4 13.8-5.9 7-13.3 18.5-13.3 31.5 0 .9.2 1.9.2 2.2.6.1 1.5.2 2.4.2 8.1 0 18.2-5.4 22.7-13.9z"/></svg>
            {t('apple', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}